import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CommandPaletteComponent } from './command-palette.component';
import {
  OwnerGraphPaletteComponent,
  OwnerGraphPaletteData
} from '../owner-graph/owner-graph-palette.component';
import { currentRecord } from '../../utils/current-record';
import { GraphAnchor } from '../../../services/owner-graph.service';
import { CommandPaletteData, isPaletteSwitch } from './palette-switch';

type PaletteKind = 'search' | 'graph';

/**
 * Ouvre et ferme les deux palettes : la recherche (Cmd+K) et le graphe du
 * propriétaire (Cmd+Shift+K). Les raccourcis vivent dans `LayoutComponent` :
 * la coque des écrans connectés, et elle seule.
 *
 * Une seule palette à la fois. Le raccourci de la palette ouverte la referme ;
 * celui de l'autre la remplace — AVEC son contexte : Cmd+Shift+K depuis la
 * recherche centre le graphe sur la ligne active (la recherche le signale en
 * se fermant, voir `PaletteSwitch`), et Cmd+K depuis le graphe rouvre la
 * recherche sur la saisie précédente. Le sélecteur « Recherche | Graphe » du
 * pied des deux palettes fait la même chose au clic (`PaletteSwitcherComponent`). MatDialog fournit le reste : Échap, piège à
 * focus, retour du focus à l'élément d'origine, fermeture à la navigation.
 */
@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private ref: MatDialogRef<unknown> | null = null;
  private current: PaletteKind | null = null;
  /** La dernière saisie de la recherche, rendue à sa réouverture. Vit le temps de l'onglet. */
  private lastQuery = '';

  toggle(): void {
    this.toggleKind('search');
  }

  toggleGraph(): void {
    this.toggleKind('graph');
  }

  private toggleKind(kind: PaletteKind): void {
    if (this.ref && this.current === kind) {
      this.ref.close();
      return;
    }
    this.ref?.close();
    this.open(kind);
  }

  private open(kind: PaletteKind, anchor?: GraphAnchor | null): void {
    const base: MatDialogConfig = {
      maxWidth: '94vw',
      position: { top: kind === 'search' ? '12vh' : '6vh' },
      panelClass: 'command-palette-panel',
      restoreFocus: true,
    };

    const ref: MatDialogRef<unknown> = kind === 'search'
      ? this.dialog.open<CommandPaletteComponent, CommandPaletteData>(CommandPaletteComponent, {
          ...base,
          width: '640px',
          autoFocus: 'first-tabbable',
          data: { query: this.lastQuery },
        })
      : this.dialog.open<OwnerGraphPaletteComponent, OwnerGraphPaletteData>(OwnerGraphPaletteComponent, {
          ...base,
          width: '1240px',
          // Le composant pose lui-même le focus (graphe ou recherche) une fois chargé.
          autoFocus: false,
          data: { anchor: anchor ?? currentRecord(this.router.url) },
        });

    this.ref = ref;
    this.current = kind;
    if (kind === 'search') {
      // Relevée avant la destruction du composant, quelle que soit la sortie
      // (Échap, clic, navigation, passage au graphe).
      ref.beforeClosed().subscribe(() => {
        this.lastQuery = (ref.componentInstance as CommandPaletteComponent).query();
      });
    }
    ref.afterClosed().subscribe(result => {
      if (this.ref === ref) {
        this.ref = null;
        this.current = null;
      }
      if (!isPaletteSwitch(result)) return;
      if (result.switchTo === 'graph') this.open('graph', result.anchor);
      else this.open('search');
    });
  }
}
